import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

type DecimalString = string | null;

export interface SpreadPlRow {
  hedgeRequestId: string | null;
  orderId: string | null;
  ticketId: number | null;
  hedgeMetal: string | null;
  instrumentType: string | null;
  currencyPair: string | null;
  closeExecutionId: string | null;
  openExecutionId: string | null;
  closeQty: DecimalString;
  closePrice: DecimalString;
  closePromptDate: string | null;
  closeExecutionDate: string | null;
  closePromptMonth: string | null;
  openQty: DecimalString;
  openPrice: DecimalString;
  openPromptDate: string | null;
  openExecutionDate: string | null;
  openPromptMonth: string | null;
  spreadPl: DecimalString;
  matchedQty: DecimalString;
  spreadDays: number | null;
  executionMonth: string | null;
}

export class HedgingSpreadPlApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingSpreadPlApiError';
  }
}

export type SpreadPlInstrument = 'METAL' | 'FX';

async function fetchSpreadPl(
  metal: string | null,
  instrument: SpreadPlInstrument,
): Promise<SpreadPlRow[]> {
  const url = new URL(`${getApiBaseUrl()}/hedging/spread-pl`);
  if (metal) url.searchParams.set('metal', metal);
  url.searchParams.set('instrument', instrument);
  const response = await authFetch(url.toString());
  if (!response.ok) {
    throw new HedgingSpreadPlApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as SpreadPlRow[];
}

export const HEDGING_SPREAD_PL_QUERY_KEY = ['hedging', 'spread-pl'] as const;

export function useHedgingSpreadPl(
  metal: string | null = null,
  instrument: SpreadPlInstrument = 'METAL',
) {
  return useQuery({
    queryKey: [...HEDGING_SPREAD_PL_QUERY_KEY, metal, instrument],
    queryFn: () => fetchSpreadPl(metal, instrument),
  });
}
